/* =========================================================
   JUBI · Rug Size Guide — interactive canvas
   Phase 1. Vanilla JS, no build step. One file, shape switch.

   Mounts onto every .sgj[data-mount] element on the page.
   Reads a sibling <script type="application/json"
   data-sgj-data="{mountId}"> for its config.
   ========================================================= */
(function () {
  'use strict';

  /* ----------------------------------------------------------
     Shared constants + style tokens
     ---------------------------------------------------------- */
  var PPF = 11;
  var INK = 'var(--jubi-ink)';
  var PAPER = 'var(--jubi-paper)';
  var BRONZE = 'var(--jubi-bronze)';
  var BG2 = 'var(--jubi-bone)';

  var STROKE_MAIN =
    'stroke="' + INK + '" stroke-width="1.25" fill="' + PAPER +
    '" stroke-linejoin="round" stroke-linecap="round"';

  /* ----------------------------------------------------------
     SVG helpers
     ---------------------------------------------------------- */
  function r2(n) { return Math.round(n * 100) / 100; }

  function rect(x, y, w, h, rx) {
    return (
      '<rect x="' + r2(x) + '" y="' + r2(y) +
      '" width="' + r2(w) + '" height="' + r2(h) +
      (rx ? '" rx="' + rx : '') + '" ' + STROKE_MAIN + '/>'
    );
  }
  function cushion(x, y, w, h, rx) {
    if (rx == null) rx = 3;
    return (
      '<rect x="' + r2(x) + '" y="' + r2(y) +
      '" width="' + r2(w) + '" height="' + r2(h) +
      '" rx="' + rx + '" fill="' + BG2 +
      '" stroke="' + INK + '" stroke-width="0.75" opacity="0.7"/>'
    );
  }
  function circle(cx, cy, rr) {
    return (
      '<circle cx="' + r2(cx) + '" cy="' + r2(cy) +
      '" r="' + r2(rr) + '" ' + STROKE_MAIN + '/>'
    );
  }
  function ring(cx, cy, rr, opacity) {
    if (opacity == null) opacity = 0.5;
    return (
      '<circle cx="' + r2(cx) + '" cy="' + r2(cy) +
      '" r="' + r2(rr) + '" fill="none" stroke="' + INK +
      '" stroke-width="0.75" opacity="' + opacity + '"/>'
    );
  }
  function ellipse(cx, cy, rx, ry) {
    return (
      '<ellipse cx="' + r2(cx) + '" cy="' + r2(cy) +
      '" rx="' + r2(rx) + '" ry="' + r2(ry) +
      '" ' + STROKE_MAIN + '/>'
    );
  }
  function ellipseFaint(cx, cy, rx, ry, opacity) {
    if (opacity == null) opacity = 0.5;
    return (
      '<ellipse cx="' + r2(cx) + '" cy="' + r2(cy) +
      '" rx="' + r2(rx) + '" ry="' + r2(ry) +
      '" fill="none" stroke="' + INK +
      '" stroke-width="0.75" opacity="' + opacity + '"/>'
    );
  }
  function line(x1, y1, x2, y2, opacity, sw) {
    if (opacity == null) opacity = 0.5;
    if (sw == null) sw = 0.75;
    return (
      '<line x1="' + r2(x1) + '" y1="' + r2(y1) +
      '" x2="' + r2(x2) + '" y2="' + r2(y2) +
      '" stroke="' + INK + '" stroke-width="' + sw +
      '" opacity="' + opacity + '"/>'
    );
  }
  function lineBold(x1, y1, x2, y2) {
    return (
      '<line x1="' + r2(x1) + '" y1="' + r2(y1) +
      '" x2="' + r2(x2) + '" y2="' + r2(y2) +
      '" stroke="' + INK + '" stroke-width="1.25"/>'
    );
  }
  function wall(x1, y1, x2, y2) {
    return (
      '<line x1="' + r2(x1) + '" y1="' + r2(y1) +
      '" x2="' + r2(x2) + '" y2="' + r2(y2) +
      '" stroke="' + INK + '" stroke-width="1.5"/>'
    );
  }
  function pathFaintDashed(d) {
    return (
      '<path d="' + d + '" fill="none" stroke="' + INK +
      '" stroke-width="0.75" opacity="0.5" stroke-dasharray="2 3"/>'
    );
  }
  function fmtSize(w, h) { return w + "' × " + h + "'"; }
  function fmtFt(n) { return n + "'"; }
  function escapeAttr(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /* ----------------------------------------------------------
     Reusable furniture: King bed (top-of-room, headboard flush)
     Used for rectangle/round/square/oval .bedroom AND runner.foot-of-bed.
     ---------------------------------------------------------- */
  // Bed drawing, parameterized by mattress footprint (ft) and nightstand
  // count so one function serves king/queen/full/twin room variants.
  function makeBed(bedWf, bedHf, nightstands) {
    return function (p, rw, rh) {
      var bedW = bedWf * p, bedH = bedHf * p;
      var bedX = (rw * p - bedW) / 2, bedY = 1 * p;
      var nsW = 2 * p, nsH = 1.5 * p;
      var out =
        rect(bedX - 0.4 * p, bedY - 0.4 * p, bedW + 0.8 * p, 0.5 * p, 0) +
        rect(bedX, bedY, bedW, bedH, 3);
      if (bedWf >= 4.5) {
        out +=
          cushion(bedX + 0.5 * p, bedY + 0.4 * p, bedW / 2 - 0.75 * p, 1.2 * p, 3) +
          cushion(bedX + bedW / 2 + 0.25 * p, bedY + 0.4 * p, bedW / 2 - 0.75 * p, 1.2 * p, 3);
      } else {
        out += cushion(bedX + 0.5 * p, bedY + 0.4 * p, bedW - 1 * p, 1.2 * p, 3);
      }
      out += line(bedX + 4, bedY + 2.8 * p, bedX + bedW - 4, bedY + 2.8 * p, 0.4);
      out += rect(bedX + bedW + 0.3 * p, bedY, nsW, nsH, 0);
      if (nightstands > 1) {
        out += rect(bedX - nsW - 0.3 * p, bedY, nsW, nsH, 0);
      }
      return out;
    };
  }
  var kingBed = makeBed(6.5, 7, 2);
  var queenBed = makeBed(5, 6.7, 2);
  var fullBed = makeBed(4.5, 6.3, 1);
  var twinBed = makeBed(3.2, 6.3, 1);

  function doorSwingBottomCenter(p, rw, rh) {
    var cx = rw * p / 2;
    var doorHalf = 1.5 * p;
    var arcR = 3 * p;
    return (
      pathFaintDashed(
        'M ' + (cx - doorHalf) + ' ' + (rh * p) +
        ' A ' + arcR + ' ' + arcR + ' 0 0 1 ' +
        (cx + doorHalf) + ' ' + (rh * p - arcR)
      ) +
      lineBold(cx - doorHalf, rh * p, cx - doorHalf, rh * p - arcR)
    );
  }

  /* ----------------------------------------------------------
     Room dimensions (feet) — one entry per shape × room
     ---------------------------------------------------------- */
  var ROOM_DIMS = {
    rectangle: {
      living:  { w: 18, h: 14 },
      bedroom: { w: 16, h: 14 },
      'bedroom-queen': { w: 15, h: 13 },
      'bedroom-full':  { w: 14, h: 12 },
      'bedroom-twin':  { w: 12, h: 11 },
      office:  { w: 14, h: 11 },
      dining:  { w: 16, h: 14 }
    },
    round: {
      dining:  { w: 16, h: 14 },
      entry:   { w: 12, h: 10 },
      bedroom: { w: 16, h: 14 },
      reading: { w: 12, h: 10 }
    },
    square: {
      dining:  { w: 16, h: 14 },
      living:  { w: 18, h: 14 },
      bedroom: { w: 16, h: 14 },
      entry:   { w: 12, h: 10 }
    },
    oval: {
      dining:  { w: 18, h: 14 },
      living:  { w: 18, h: 14 },
      bedroom: { w: 16, h: 14 },
      entry:   { w: 14, h: 10 }
    },
    corner: {
      'reading':      { w: 14, h: 12 },
      'l-sofa':       { w: 18, h: 14 },
      'bed-corner':   { w: 18, h: 14 },
      'entry-corner': { w: 12, h: 10 }
    },
    runner: {
      'hallway':     { w: 10, h: 18 },
      'kitchen':     { w: 12, h: 18 },
      'foot-of-bed': { w: 16, h: 12 },
      'landing':     { w: 12, h: 21 },
      'entry':       { w: 9,  h: 10 }
    }
  };

  // irregular reuses rectangle's room dimensions
  ROOM_DIMS.irregular = ROOM_DIMS.rectangle;

  /* ----------------------------------------------------------
     Rug placement (feet) — per shape × room.

     Returns either a rectangular placement { x, y, w, h }
     or (shape=corner) { corner:true, anchorX, anchorY, orient, side }.
     ---------------------------------------------------------- */
  var PLACEMENT = {
    rectangle: function (room, eff, rw, rh) {
      var cy;
      if (room === 'living') {
        // Anchor so the sofa's front legs sit on the rug. Sofa front
        // edge is at y=4ft; aim rug top ~1ft below that (under the sofa
        // front), then clamp to fit the room.
        cy = Math.max(eff.h / 2, Math.min(3 + eff.h / 2, rh - eff.h / 2));
      } else if (room.indexOf('bedroom') === 0) {
        // All bed sizes share the tiering — beds start at y=1, so the
        // rug-top tiers read correctly for king through twin.
        // Bedroom placement is tiered on the rug's SHORTER edge (invariant
        // to orientation) so the visual arrangement matches the fit verdict
        // copy regardless of portrait/landscape.
        //   < 4.5'  — tiny accent: sit just below the bed (no overlap)
        //   < 6'    — foot of bed: partial overlap with bed bottom
        //   < 9'    — three-sided wrap: rug top BELOW nightstands (y=3)
        //   < 11'   — full wrap: rug top under the nightstands (y=1)
        //   else    — oversized: rug top at room top
        // Bed geometry: bed at y=1–8, nightstands at y=1–2.5.
        var shortEdge = Math.min(eff.w, eff.h);
        // Bedside runner: a small, elongated rug (3×5 by a king, 2.5×7 by a
        // twin) is a bedside runner in the copy — not a foot rug. Draw it
        // vertical, tucked against the LEFT side of the bed instead of centered
        // at the foot. Matches the "Bedside pair / runner" verdict + the
        // engine's own "Bedside Accent" rectangle verdict.
        if (shortEdge < 4.5 && Math.max(eff.w, eff.h) / shortEdge >= 1.6) {
          var BED_BY_ROOM = {
            bedroom: { wf: 6.5, ns: 2 }, 'bedroom-queen': { wf: 5, ns: 2 },
            'bedroom-full': { wf: 4.5, ns: 1 }, 'bedroom-twin': { wf: 3.2, ns: 1 }
          };
          var bd = BED_BY_ROOM[room] || { wf: 5, ns: 2 };
          var bsW = shortEdge, bsH = Math.max(eff.w, eff.h);
          var bsX = Math.max(0.5, (rw - bd.wf) / 2 - bsW);
          var bsTop = Math.max(0.5, Math.min(bd.ns > 1 ? 2.6 : 1.2, rh - bsH - 0.5));
          var bsP = { x: bsX, y: bsTop, w: bsW, h: bsH };
          // Beds with nightstands both sides (king/queen) show the bedside
          // runner as a PAIR — mirror a second runner on the right side.
          if (bd.ns > 1) { bsP.pairX = rw - bsX - bsW; }
          return bsP;
        }
        var rugTop;
        if (shortEdge < 4.5)      rugTop = 8;
        else if (shortEdge < 6)   rugTop = 6.5;
        else if (shortEdge < 9)   rugTop = 3;
        else if (shortEdge < 11)  rugTop = 1;
        else                       rugTop = 0;
        cy = rugTop + eff.h / 2;
        cy = Math.max(eff.h / 2, Math.min(cy, rh - eff.h / 2));
      } else if (room === 'office') {
        // Desk at y=1-3.2, chair to ~5.6. Small rugs sit under the chair
        // zone; larger ones tuck under the desk front so the chair's full
        // range stays on the rug.
        var seO = Math.min(eff.w, eff.h);
        var rugTopO = seO < 5 ? 2.6 : 1.6;
        cy = rugTopO + eff.h / 2;
        cy = Math.max(eff.h / 2, Math.min(cy, rh - eff.h / 2));
      } else {
        cy = rh / 2;
      }
      return { x: (rw - eff.w) / 2, y: cy - eff.h / 2, w: eff.w, h: eff.h };
    },
    round: function (room, eff, rw, rh) {
      var d = eff.w;
      var cx = rw / 2, cy = rh / 2;
      if (room === 'bedroom') {
        // Bed at y=1–8. Rounds taper at the corners, so nightstands never
        // actually sit on the rug — placement tiers stagger so smaller
        // rounds stay clearly past the foot, larger ones reach under:
        //   < 5'  — bedside accent: rug top y=7.5 (mostly past the foot)
        //   < 7'  — foot of bed (small): rug top y=6 (visible landing past)
        //   < 9'  — foot of bed (larger): rug top y=4 (reaches under)
        //   < 13' — anchors the bed: rug top y=1 (visible past sides + foot)
        //   else  — oversized: rug top at room top
        var rugTop;
        if (d < 5)        rugTop = 7.5;
        else if (d < 7)   rugTop = 6;
        else if (d < 9)   rugTop = 4;
        else if (d < 13)  rugTop = 1;
        else              rugTop = 0;
        cy = rugTop + d / 2;
        cy = Math.max(d / 2, Math.min(cy, rh - d / 2));
      }
      else if (room === 'reading') cy = rh / 2 + 0.5;
      return { x: cx - d / 2, y: cy - d / 2, w: d, h: d };
    },
    square: function (room, eff, rw, rh) {
      var cx = rw / 2, cy = rh / 2;
      if (room === 'bedroom') {
        // Smooth gradient: each rug step reaches 1ft further under the bed
        // until it hits the headboard. Past-foot visibility stays ~3ft, so
        // the visual progression is just "rug grows toward the headboard".
        //   < 5'      — bedside accent: rug top y=7.5 (mostly past the foot)
        //   5'-10'    — foot of bed → anchors: rug top = 11 - s (gradient)
        //   11'-12'   — anchors at headboard: rug top y=1
        //   13'+      — oversized: rug top at room top
        var s = eff.w;
        var rugTop;
        if (s < 5)        rugTop = 7.5;
        else if (s <= 10) rugTop = 11 - s;
        else if (s < 13)  rugTop = 1;
        else              rugTop = 0;
        cy = rugTop + s / 2;
        cy = Math.max(s / 2, Math.min(cy, rh - s / 2));
      }
      return { x: cx - eff.w / 2, y: cy - eff.h / 2, w: eff.w, h: eff.h };
    },
    oval: function (room, eff, rw, rh) {
      var cx = rw / 2, cy = rh / 2;
      if (room === 'living')  cy = rh / 2 + 1;
      if (room === 'bedroom') {
        // Smooth gradient on rug HEIGHT (along the bed axis): each step
        // reaches 1ft further toward the headboard, so the visual
        // progression is "rug grows under the bed" as size increases.
        // Keeps placement consistent across orientations.
        var h = eff.h;
        var rugTop;
        if (h < 5)        rugTop = 7.5;
        else if (h <= 10) rugTop = 11 - h;
        else if (h < 13)  rugTop = 1;
        else              rugTop = 0;
        cy = rugTop + h / 2;
        cy = Math.max(h / 2, Math.min(cy, rh - h / 2));
      }
      return { x: cx - eff.w / 2, y: cy - eff.h / 2, w: eff.w, h: eff.h };
    },
    corner: function (room, eff, rw /*, rh*/) {
      var side = eff.w;
      var anchors = {
        'reading':      { ax: 0,  ay: 0, orient: 'tl' },
        'l-sofa':       { ax: rw, ay: 0, orient: 'tr' },
        'bed-corner':   { ax: 0,  ay: 0, orient: 'tl' },
        'entry-corner': { ax: 0,  ay: 0, orient: 'tl' }
      };
      var a = anchors[room] || anchors['reading'];
      return { corner: true, anchorX: a.ax, anchorY: a.ay, orient: a.orient, side: side };
    },
    runner: function (room, eff, rw, rh) {
      var nw = Math.min(eff.w, eff.h);
      var lg = Math.max(eff.w, eff.h);
      if (room === 'hallway' || room === 'kitchen') {
        return { x: rw / 2 - nw / 2, y: (rh - lg) / 2, w: nw, h: lg };
      }
      if (room === 'landing') {
        return { x: rw / 2 - nw / 2, y: 4.5 + 0.1, w: nw, h: lg };
      }
      if (room === 'entry') {
        // Landing zone: rug sits above the door swing (3' radius from the
        // bottom wall), centered on the walkway.
        var ey = Math.max(0.6, rh - 3.2 - lg);
        return { x: rw / 2 - nw / 2, y: ey, w: nw, h: lg };
      }
      if (room === 'foot-of-bed') {
        return { x: rw / 2 - lg / 2, y: 1 + 7 + 0.5, w: lg, h: nw };
      }
      return { x: (rw - nw) / 2, y: (rh - lg) / 2, w: nw, h: lg };
    }
  };

  // Irregular shape uses the rectangle template's behavior end-to-end.
  // Same room set, same placement math, same furniture, same fit verdicts.
  // The only thing that's actually different is the silhouette art (which
  // is per-variant data, not code).
  PLACEMENT.irregular = PLACEMENT.rectangle;

  /* ----------------------------------------------------------
     Furniture — per shape × room. Each returns an SVG inner-
     markup string positioned inside a rw × rh room (feet),
     scaled by p (ppf).
     ---------------------------------------------------------- */
  var FURNITURE = {
    rectangle: {
      living: function (p, rw, rh) {
        var sofaW = 7 * p, sofaH = 3 * p;
        var sofaX = (rw * p - sofaW) / 2, sofaY = 1 * p;
        var tableW = 4 * p, tableH = 2 * p;
        var tableX = (rw * p - tableW) / 2, tableY = sofaY + sofaH + 2.5 * p;
        var chairW = 2.5 * p, chairH = 2.5 * p;
        return (
          rect(sofaX, sofaY, sofaW, sofaH, 4) +
          line(sofaX + sofaW / 3, sofaY + 6, sofaX + sofaW / 3, sofaY + sofaH - 6) +
          line(sofaX + 2 * sofaW / 3, sofaY + 6, sofaX + 2 * sofaW / 3, sofaY + sofaH - 6) +
          rect(tableX, tableY, tableW, tableH, 2) +
          rect(tableX - chairW - 2 * p, tableY - 0.5 * p, chairW, chairH, 4) +
          rect(tableX + tableW + 2 * p, tableY - 0.5 * p, chairW, chairH, 4)
        );
      },
      bedroom: kingBed,
      'bedroom-queen': queenBed,
      'bedroom-full': fullBed,
      'bedroom-twin': twinBed,
      office: function (p, rw, rh) {
        var deskW = 5 * p, deskH = 2.2 * p;
        var deskX = (rw * p - deskW) / 2, deskY = 1 * p;
        var chW = 1.8 * p, chH = 1.8 * p;
        var chX = (rw * p - chW) / 2, chY = deskY + deskH + 0.6 * p;
        var shW = 1.2 * p, shH = 5 * p;
        return (
          rect(deskX, deskY, deskW, deskH, 2) +
          line(deskX + deskW * 0.62, deskY + 4, deskX + deskW * 0.62, deskY + deskH - 4, 0.4) +
          rect(chX, chY, chW, chH, 6) +
          cushion(chX + 0.3 * p, chY + 0.3 * p, chW - 0.6 * p, chH - 0.6 * p, 4) +
          rect(0.6 * p, 1 * p, shW, shH, 0)
        );
      },
      dining: function (p, rw, rh) {
        var tW = 8 * p, tH = 3.5 * p;
        var tX = (rw * p - tW) / 2, tY = (rh * p - tH) / 2;
        var chW = 1.6 * p, chH = 1.5 * p;
        var out = rect(tX, tY, tW, tH, 2);
        for (var i = 0; i < 3; i++) {
          var x = tX + (tW / 3) * i + (tW / 3 - chW) / 2;
          out += rect(x, tY - chH - 0.3 * p, chW, chH, 3);
          out += rect(x, tY + tH + 0.3 * p, chW, chH, 3);
        }
        return out;
      }
    },

    round: {
      dining: function (p, rw, rh) {
        var cx = (rw / 2) * p, cy = (rh / 2) * p;
        var tableR = 2 * p;
        var chairD = 3.1 * p;
        var chW = 1.6 * p, chH = 1.5 * p;
        var out = circle(cx, cy, tableR) + ring(cx, cy, tableR * 0.35, 0.5);
        for (var i = 0; i < 6; i++) {
          var angle = (i * 60) * Math.PI / 180;
          var ccx = cx + Math.cos(angle) * chairD;
          var ccy = cy + Math.sin(angle) * chairD;
          var rot = (i * 60) + 90;
          out +=
            '<g transform="translate(' + r2(ccx) + ' ' + r2(ccy) +
            ') rotate(' + rot + ')">' +
              '<rect x="' + r2(-chW / 2) + '" y="' + r2(-chH / 2) +
              '" width="' + r2(chW) + '" height="' + r2(chH) +
              '" rx="3" ' + STROKE_MAIN + '/>' +
            '</g>';
        }
        return out;
      },
      entry: function (p, rw, rh) {
        var cx = (rw / 2) * p, cy = (rh / 2) * p;
        var tableR = 1.75 * p;
        return (
          circle(cx, cy, tableR) +
          ring(cx, cy, tableR * 0.4, 0.5) +
          doorSwingBottomCenter(p, rw, rh)
        );
      },
      bedroom: kingBed,
      reading: function (p, rw, rh) {
        var cx = (rw / 2) * p, cy = (rh / 2) * p;
        var chW = 2.8 * p, chH = 2.8 * p;
        var chX = cx - chW / 2 - 0.5 * p;
        var chY = cy - chH / 2;
        var stW = 1.5 * p, stH = 1.5 * p;
        var stX = chX + chW + 0.4 * p;
        var stY = chY + 0.6 * p;
        var lampR = 0.5 * p;
        var lampX = stX + stW + 0.4 * p + lampR;
        var lampY = stY + lampR - 0.2 * p;
        return (
          rect(chX, chY, chW, chH, 10) +
          cushion(chX + 0.35 * p, chY + 0.8 * p, chW - 0.7 * p, chH - 1.1 * p, 6) +
          rect(stX, stY, stW, stH, 2) +
          circle(lampX, lampY, lampR) +
          '<circle cx="' + r2(lampX) + '" cy="' + r2(lampY) +
          '" r="' + r2(lampR * 0.35) + '" fill="' + INK + '" opacity="0.6"/>'
        );
      }
    },

    square: {
      dining: function (p, rw, rh) {
        var cx = (rw / 2) * p, cy = (rh / 2) * p;
        var tW = 4 * p;
        var tX = cx - tW / 2, tY = cy - tW / 2;
        var chW = 1.6 * p, chH = 1.5 * p;
        var out = rect(tX, tY, tW, tW, 2);
        // Top & bottom — 2 chairs each
        for (var i = 0; i < 2; i++) {
          var x = tX + (tW / 2) * i + (tW / 2 - chW) / 2;
          out += rect(x, tY - chH - 0.3 * p, chW, chH, 3);
          out += rect(x, tY + tW + 0.3 * p, chW, chH, 3);
        }
        // Left & right — 2 rotated chairs each (swap chW/chH)
        for (var j = 0; j < 2; j++) {
          var y = tY + (tW / 2) * j + (tW / 2 - chW) / 2;
          out += rect(tX - chH - 0.3 * p, y, chH, chW, 3);
          out += rect(tX + tW + 0.3 * p, y, chH, chW, 3);
        }
        return out;
      },
      living: function (p, rw, rh) {
        var cx = (rw / 2) * p, cy = (rh / 2) * p;
        var lsW = 6 * p, lsH = 3 * p;
        var tableW = 3.5 * p;
        return (
          // Top loveseat
          rect(cx - lsW / 2, cy - tableW / 2 - lsH - 0.7 * p, lsW, lsH, 4) +
          // Bottom loveseat
          rect(cx - lsW / 2, cy + tableW / 2 + 0.7 * p, lsW, lsH, 4) +
          // Square coffee table
          rect(cx - tableW / 2, cy - tableW / 2, tableW, tableW, 2) +
          // Cushion seams
          line(cx, cy - tableW / 2 - lsH - 0.7 * p + 6, cx, cy - tableW / 2 - 0.7 * p - 6) +
          line(cx, cy + tableW / 2 + 0.7 * p + 6, cx, cy + tableW / 2 + lsH + 0.7 * p - 6)
        );
      },
      bedroom: kingBed,
      entry: function (p, rw, rh) {
        var cx = (rw / 2) * p, cy = (rh / 2) * p;
        var tW = 3.5 * p;
        return (
          rect(cx - tW / 2, cy - tW / 2, tW, tW, 2) +
          '<rect x="' + r2(cx - tW / 4) + '" y="' + r2(cy - tW / 4) +
          '" width="' + r2(tW / 2) + '" height="' + r2(tW / 2) +
          '" fill="none" stroke="' + INK + '" stroke-width="0.75" opacity="0.5"/>' +
          doorSwingBottomCenter(p, rw, rh)
        );
      }
    },

    oval: {
      dining: function (p, rw, rh) {
        var cx = (rw / 2) * p, cy = (rh / 2) * p;
        var tW = 7 * p, tH = 4 * p;
        var tX = cx - tW / 2, tY = cy - tH / 2;
        var chW = 1.6 * p, chH = 1.5 * p;
        var out =
          ellipse(cx, cy, tW / 2, tH / 2) +
          ellipseFaint(cx, cy, (tW / 2) * 0.4, (tH / 2) * 0.4);
        for (var i = 0; i < 3; i++) {
          var x = tX + (tW / 3) * i + (tW / 3 - chW) / 2;
          out += rect(x, tY - chH - 0.3 * p, chW, chH, 3);
          out += rect(x, tY + tH + 0.3 * p, chW, chH, 3);
        }
        return out;
      },
      living: function (p, rw, rh) {
        var cx = (rw / 2) * p, cy = (rh / 2) * p;
        var sofaW = 7 * p, sofaH = 3 * p;
        var sofaX = cx - sofaW / 2 - 1.2 * p;
        var sofaY = cy - 3.2 * p;
        var tW = 4 * p, tH = 2.2 * p;
        var tX = sofaX + (sofaW - tW) / 2;
        var tY = sofaY + sofaH + 1.0 * p;
        var lcW = 2.8 * p, lcH = 2.8 * p;
        var lcX = sofaX + sofaW + 1.2 * p;
        var lcY = sofaY + 0.3 * p;
        return (
          rect(sofaX, sofaY, sofaW, sofaH, 6) +
          line(sofaX + sofaW / 3, sofaY + 0.3 * p, sofaX + sofaW / 3, sofaY + sofaH - 0.3 * p) +
          line(sofaX + 2 * sofaW / 3, sofaY + 0.3 * p, sofaX + 2 * sofaW / 3, sofaY + sofaH - 0.3 * p) +
          rect(tX, tY, tW, tH, 6) +
          rect(lcX, lcY, lcW, lcH, 10) +
          cushion(lcX + 0.3 * p, lcY + 0.8 * p, lcW - 0.6 * p, lcH - 1.1 * p, 6)
        );
      },
      bedroom: kingBed,
      entry: function (p, rw, rh) {
        var cx = (rw / 2) * p;
        var consoleW = 5 * p, consoleH = 1.5 * p;
        var consoleX = cx - consoleW / 2;
        var consoleY = 0.8 * p;
        return (
          rect(consoleX, consoleY, consoleW, consoleH, 2) +
          cushion(consoleX + 0.6 * p, consoleY + 0.35 * p, 0.7 * p, 0.7 * p, 1) +
          cushion(consoleX + consoleW - 1.4 * p, consoleY + 0.3 * p, 0.9 * p, 0.85 * p, 1) +
          pathFaintDashed(
            'M ' + (cx - 1.5 * p) + ' ' + (rh * p) +
            ' A ' + (3 * p) + ' ' + (3 * p) + ' 0 0 1 ' +
            (cx + 1.5 * p) + ' ' + (rh * p - 3 * p)
          ) +
          lineBold(cx - 1.5 * p, rh * p, cx - 1.5 * p, rh * p - 3 * p)
        );
      }
    },

    corner: {
      reading: function (p /*, rw, rh*/) {
        // Accent chair nested at top-left, rotated 45° to face into the room
        var chW = 2.8 * p, chH = 2.8 * p;
        var anchorX = 1.8 * p, anchorY = 1.8 * p;
        var cx = anchorX + chW / 2, cy = anchorY + chH / 2;
        return (
          '<g transform="translate(' + r2(cx) + ' ' + r2(cy) +
          ') rotate(45) translate(' + r2(-chW / 2) + ' ' + r2(-chH / 2) + ')">' +
            '<rect x="0" y="0" width="' + r2(chW) + '" height="' + r2(chH) +
            '" rx="10" ' + STROKE_MAIN + '/>' +
            '<rect x="' + r2(0.35 * p) + '" y="' + r2(0.8 * p) +
            '" width="' + r2(chW - 0.7 * p) + '" height="' + r2(chH - 1.1 * p) +
            '" rx="6" fill="' + BG2 +
            '" stroke="' + INK + '" stroke-width="0.75" opacity="0.7"/>' +
          '</g>'
        );
      },
      'l-sofa': function (p, rw /*, rh*/) {
        // L-sectional tucked into the top-right corner
        var longL = 8 * p, shortL = 6 * p;
        var depth = 3 * p;
        var rightX = rw * p;
        var out =
          rect(rightX - longL, 0, longL, depth, 3) +
          rect(rightX - depth, depth, depth, shortL - depth, 3);
        // Top cushions (3)
        for (var i = 0; i < 3; i++) {
          out += cushion(
            rightX - longL + 0.3 * p + i * (longL - 0.6 * p) / 3,
            0.6 * p,
            (longL - 0.6 * p) / 3 - 0.2 * p,
            depth - 1.1 * p,
            4
          );
        }
        // Right-arm cushions (2)
        for (var j = 0; j < 2; j++) {
          out += cushion(
            rightX - depth + 0.6 * p,
            depth + 0.3 * p + j * (shortL - depth - 0.6 * p) / 2,
            depth - 1.1 * p,
            (shortL - depth - 0.6 * p) / 2 - 0.2 * p,
            4
          );
        }
        return out;
      },
      'bed-corner': function (p /*, rw, rh*/) {
        // Queen bed flush with top-left corner
        var bedW = 5 * p, bedH = 7 * p;
        var bedX = 0, bedY = 0.4 * p;
        return (
          rect(bedX, 0, bedW, 0.4 * p, 0) +
          rect(bedX, bedY, bedW, bedH, 3) +
          cushion(bedX + 0.4 * p, bedY + 0.4 * p, bedW / 2 - 0.6 * p, 1.2 * p, 3) +
          cushion(bedX + bedW / 2 + 0.2 * p, bedY + 0.4 * p, bedW / 2 - 0.6 * p, 1.2 * p, 3) +
          line(bedX + 4, bedY + 2.4 * p, bedX + bedW - 4, bedY + 2.4 * p, 0.4)
        );
      },
      'entry-corner': function (p, rw, rh) {
        // Console on the right wall, door swing at bottom-right corner
        var conW = 1.5 * p, conH = 4 * p;
        var conX = rw * p - conW - 0.2 * p;
        var conY = (rh * p - conH) / 2;
        return (
          rect(conX, conY, conW, conH, 2) +
          pathFaintDashed(
            'M ' + (rw * p) + ' ' + (rh * p) +
            ' A ' + (3 * p) + ' ' + (3 * p) + ' 0 0 0 ' +
            (rw * p - 3 * p) + ' ' + (rh * p - 3 * p)
          ) +
          lineBold(rw * p - 3 * p, rh * p, rw * p - 3 * p, rh * p - 3 * p)
        );
      }
    },

    runner: {
      entry: function (p, rw, rh) {
        var conW = 1.2 * p, conH = 3.5 * p;
        return (
          rect(0.5 * p, 1 * p, conW, conH, 0) +
          doorSwingBottomCenter(p, rw, rh)
        );
      },
      hallway: function (p, rw, rh) {
        // 4'-wide hallway: walls on either side with door openings
        var wallX = (rw - 4) / 2;
        var leftWall = wallX * p;
        var rightWall = (rw - wallX) * p;
        return (
          wall(leftWall, 0, leftWall, 3 * p) +
          wall(leftWall, 6 * p, leftWall, 11 * p) +
          wall(leftWall, 14 * p, leftWall, rh * p) +
          pathFaintDashed('M ' + leftWall + ' ' + (3 * p) + ' A ' + (3 * p) + ' ' + (3 * p) + ' 0 0 0 ' + (leftWall + 3 * p) + ' ' + (6 * p)) +
          pathFaintDashed('M ' + leftWall + ' ' + (11 * p) + ' A ' + (3 * p) + ' ' + (3 * p) + ' 0 0 0 ' + (leftWall + 3 * p) + ' ' + (14 * p)) +
          wall(rightWall, 0, rightWall, 7 * p) +
          wall(rightWall, 10 * p, rightWall, rh * p) +
          pathFaintDashed('M ' + rightWall + ' ' + (7 * p) + ' A ' + (3 * p) + ' ' + (3 * p) + ' 0 0 1 ' + (rightWall - 3 * p) + ' ' + (10 * p))
        );
      },
      kitchen: function (p, rw, rh) {
        // Galley kitchen: 2'-deep base cabinets on both sides with sink + range
        var cabDepth = 2;
        var leftCabX = 0, rightCabX = (rw - cabDepth) * p;
        var out =
          rect(leftCabX, 0, cabDepth * p, rh * p, 0) +
          cushion(0.3 * p, 5 * p, 1.4 * p, 2 * p, 2) +
          '<circle cx="' + r2(p) + '" cy="' + r2(5.3 * p) + '" r="3" fill="' + INK + '" opacity="0.5"/>' +
          cushion(0.3 * p, 10 * p, 1.4 * p, 1.8 * p, 2) +
          ring(0.75 * p, 10.5 * p, 2.5, 0.5) +
          ring(1.25 * p, 10.5 * p, 2.5, 0.5) +
          ring(0.75 * p, 11.2 * p, 2.5, 0.5) +
          ring(1.25 * p, 11.2 * p, 2.5, 0.5) +
          rect(rightCabX, 0, cabDepth * p, rh * p, 0);
        [3, 7, 12, 15].forEach(function (y) {
          out += line(rightCabX, y * p, rightCabX + cabDepth * p, y * p, 0.5);
        });
        [3, 12].forEach(function (y) {
          out += line(0, y * p, cabDepth * p, y * p, 0.5);
        });
        return out;
      },
      'foot-of-bed': kingBed,
      landing: function (p, rw /*, rh*/) {
        var cx = (rw / 2) * p;
        var landW = 4 * p, landH = 4 * p;
        var landX = cx - landW / 2, landY = 0.5 * p;
        var out =
          '<rect x="' + r2(landX) + '" y="' + r2(landY) +
          '" width="' + r2(landW) + '" height="' + r2(landH) +
          '" fill="none" stroke="' + INK +
          '" stroke-width="1.25" stroke-dasharray="4 3"/>' +
          '<text x="' + r2(cx) + '" y="' + r2(landY + landH / 2 + 4) +
          '" text-anchor="middle" font-family="Helvetica, Arial, sans-serif"' +
          ' font-size="10" letter-spacing="1.5" fill="' + BRONZE +
          '" style="text-transform:uppercase;">Landing</text>';
        for (var i = 0; i < 3; i++) {
          out += line(landX, landY - 0.3 * p - i * 0.4 * p, landX + landW, landY - 0.3 * p - i * 0.4 * p, 0.5);
        }
        return out;
      }
    }
  };

  // irregular reuses rectangle's furniture
  FURNITURE.irregular = FURNITURE.rectangle;

  /* ----------------------------------------------------------
     Fit verdicts — each returns {tone, label, copy}.
     Ported 1:1 from prototype fitVerdict* functions.
     ---------------------------------------------------------- */
  var FIT_VERDICT = {
    rectangle: function (room, w, h) {
      var a = w * h;
      var label = fmtSize(w, h);
      if (room === 'living') {
        if (a < 30)  return { tone: 'small', label: 'Too Small',   copy: 'This size reads as an accent. For a living-room rug, aim for at least 8×10 so furniture relates to it.' };
        if (a < 70)  return { tone: 'tight', label: 'Front Legs',  copy: 'A ' + label + ' fits with the front legs of the sofa on the rug. Chairs sit off.' };
        if (a < 110) return { tone: 'ideal', label: 'Ideal Fit',   copy: 'A ' + label + ' fits your sofa plus front chair legs, with walking room on all sides.' };
        if (a < 160) return { tone: 'ideal', label: 'All On',      copy: 'A ' + label + ' places the full seating group on the rug — a generous, grounded arrangement.' };
        return               { tone: 'tight', label: 'Too Large',  copy: 'A ' + label + ' may overwhelm most living rooms. Great for open-plan or showroom-scale spaces.' };
      }
      if (room === 'bedroom') {
        // Tiered on the SHORTER edge so the verdict matches the placement.
        var shortEdge = Math.min(w, h);
        if (shortEdge < 4.5) return { tone: 'small', label: 'Bedside Accent', copy: 'A ' + label + ' works as a bedside accent or layered atop a larger rug — the bed itself sits off the rug.' };
        if (shortEdge < 6)   return { tone: 'tight', label: 'Foot of Bed',    copy: 'A ' + label + ' anchors the foot of a queen or king bed — a softer, partial layout where the bed mostly sits off the rug.' };
        if (shortEdge < 9)   return { tone: 'ideal', label: 'Ideal Fit',      copy: 'A ' + label + ' extends past the bed on three sides — the classic under-bed arrangement, with the headboard and nightstands sitting off.' };
        return                 { tone: 'ideal', label: 'Full Wrap',           copy: 'A ' + label + ' wraps well past a king bed with the headboard and nightstands on the rug. Luxurious in a larger room.' };
      }
      if (room === 'dining') {
        if (a < 30)  return { tone: 'small', label: 'Too Small',    copy: 'Dining rugs need to hold chairs even when pulled out. For a 4-seat table aim for at least 6×9; for a 6–8 seat table, 9×12 or larger.' };
        if (a < 60)  return { tone: 'tight', label: 'Smaller Table', copy: 'A ' + label + ' suits a 4-seat dining setup (4\'–5\' round or square table). For a 6–8 seat table on an 8\' rectangle, size up to 9×12.' };
        if (a < 90)  return { tone: 'tight', label: 'Tight',         copy: 'A ' + label + ' fits a 4–6 seat table comfortably, but chairs may catch the edge when pulled out from a full 8\' table. 9×12 is more forgiving.' };
        if (a < 140) return { tone: 'ideal', label: 'Ideal Fit',     copy: 'A ' + label + ' holds a 6–8 seat table with chairs comfortably on the rug, pulled out or in.' };
        return               { tone: 'ideal', label: 'Large Party',  copy: 'A ' + label + ' accommodates an 8–10 seat table with room to spare.' };
      }
      return { tone: 'ideal', label: '', copy: '' };
    },

    round: function (room, w /*, h equal */) {
      var d = w;
      var lbl = fmtFt(d);
      if (room === 'dining') {
        if (d < 6)  return { tone: 'small', label: 'Too Small', copy: 'A ' + lbl + ' rug sits under the table only — chairs slip off when pulled out. Aim for 8\' or more.' };
        if (d < 8)  return { tone: 'tight', label: 'Tight',     copy: 'A ' + lbl + ' round gives chairs a partial landing. For a 4\' table, 9\'–10\' is the ideal.' };
        if (d < 11) return { tone: 'ideal', label: 'Ideal Fit', copy: 'A ' + lbl + ' round extends roughly 30" past a 4\' pedestal table — chairs stay on when pulled out.' };
        return             { tone: 'tight', label: 'Oversized', copy: 'A ' + lbl + ' round works for great-room dining. In a typical room, 9\'–10\' is usually the sweet spot.' };
      }
      if (room === 'entry') {
        if (d < 4)  return { tone: 'small', label: 'Too Small', copy: 'A ' + lbl + ' round reads as an accent. For a proper foyer, aim for 5\'–7\'.' };
        if (d < 8)  return { tone: 'ideal', label: 'Ideal Fit', copy: 'A ' + lbl + ' round anchors a foyer beneath a center table or chandelier. Check door-swing clearance.' };
        return             { tone: 'tight', label: 'Too Large', copy: 'A ' + lbl + ' round is likely larger than most entries need. Works best in grand foyers.' };
      }
      if (room === 'bedroom') {
        if (d < 5)   return { tone: 'small', label: 'Bedside Accent',  copy: 'A ' + lbl + ' round reads as a small accent at the foot of the bed. For a true foot-of-bed rug, go 5\'–8\'; to anchor the bed, 9\' or larger.' };
        if (d < 7)   return { tone: 'tight', label: 'Foot of Bed',     copy: 'A ' + lbl + ' round sits at the foot of the bed with a light overlap. Reads as a small landing against a king — 7\'–8\' gives a fuller foot rug.' };
        if (d < 9)   return { tone: 'ideal', label: 'Foot of Bed',     copy: 'A ' + lbl + ' round anchors the foot of the bed — extends past the foot and reaches under the mattress.' };
        if (d < 13)  return { tone: 'ideal', label: 'Anchors the Bed', copy: 'A ' + lbl + ' round reaches under the bed with the rug visible on both sides and extending past the foot. Nightstands sit off — rounds don\'t fully wrap.' };
        return              { tone: 'tight', label: 'Too Large',       copy: 'A ' + lbl + ' round is larger than most bedrooms can hold. Consider 9\'–12\' for a generous look.' };
      }
      if (room === 'reading') {
        if (d < 4)  return { tone: 'small', label: 'Too Small', copy: 'A ' + lbl + ' round is tight under an accent chair. 5\'–7\' lets the chair and side table sit on fully.' };
        if (d < 8)  return { tone: 'ideal', label: 'Ideal Fit', copy: 'A ' + lbl + ' round anchors a reading nook — chair and side table rest on, with breathing room.' };
        return             { tone: 'tight', label: 'Large',     copy: 'A ' + lbl + ' round is generous for a single chair scene. Consider layering, or use in a conversation area.' };
      }
      return { tone: 'ideal', label: '', copy: '' };
    },

    square: function (room, w /*, h equal */) {
      var s = w;
      var lbl = fmtFt(s);
      if (room === 'dining') {
        if (s < 7)  return { tone: 'small', label: 'Too Small', copy: 'A ' + lbl + ' square sits under the table only — chairs slip off when pulled out. Aim for 9–10\'.' };
        if (s < 9)  return { tone: 'tight', label: 'Tight',     copy: 'A ' + lbl + ' square gives a partial chair landing for a 4\' table. 9\' or 10\' is more forgiving.' };
        if (s < 12) return { tone: 'ideal', label: 'Ideal Fit', copy: 'A ' + lbl + ' square extends roughly 30" past a 4\' table — chairs stay on when pulled out.' };
        return             { tone: 'tight', label: 'Oversized', copy: 'A ' + lbl + ' square works in great-room dining. In a standard room, 10\' is usually the sweet spot.' };
      }
      if (room === 'living') {
        if (s < 6)  return { tone: 'small', label: 'Too Small',  copy: 'A ' + lbl + ' square reads as an accent. For a conversational seating group, aim for 8–10\'.' };
        if (s < 8)  return { tone: 'tight', label: 'Front Legs', copy: 'An ' + lbl + ' square fits with the front legs of both seats on the rug — a looser, partial anchor.' };
        if (s < 12) return { tone: 'ideal', label: 'Ideal Fit',  copy: 'A ' + lbl + ' square anchors a symmetrical seating group with the coffee table fully on.' };
        return             { tone: 'tight', label: 'Large',      copy: 'A ' + lbl + ' square works for great-room scale. Check that walking lanes aren\'t cramped.' };
      }
      if (room === 'bedroom') {
        if (s < 5)   return { tone: 'small', label: 'Bedside Accent',  copy: 'A ' + lbl + ' square reads as a small accent at the foot of the bed. For a true foot-of-bed rug, go 5–8\'; to anchor the bed, 9\' or larger.' };
        if (s < 7)   return { tone: 'tight', label: 'Foot of Bed',     copy: 'A ' + lbl + ' square sits at the foot of the bed with a light overlap. Reads small against a king — 7–8\' gives a fuller foot rug.' };
        if (s < 9)   return { tone: 'ideal', label: 'Foot of Bed',     copy: 'A ' + lbl + ' square anchors the foot of the bed — extends past the foot and reaches under the mattress.' };
        if (s < 12)  return { tone: 'ideal', label: 'Anchors the Bed', copy: 'A ' + lbl + ' square anchors the bed toward the headboard, with generous landing on both sides and past the foot. Nightstands sit off.' };
        if (s < 13)  return { tone: 'ideal', label: 'Full Wrap',       copy: 'A ' + lbl + ' square wraps the bed entirely — nightstands sit on, with the rug extending past the foot.' };
        return              { tone: 'tight', label: 'Too Large',       copy: 'A ' + lbl + ' square is larger than most bedrooms can hold. Consider 9–12\' for a generous look.' };
      }
      if (room === 'entry') {
        if (s < 4)  return { tone: 'small', label: 'Too Small', copy: 'A ' + lbl + ' square reads as an accent. For a proper foyer, aim for 5–7\'.' };
        if (s < 8)  return { tone: 'ideal', label: 'Ideal Fit', copy: 'A ' + lbl + ' square anchors a foyer beneath a center table or chandelier. Check door-swing clearance.' };
        return             { tone: 'tight', label: 'Too Large', copy: 'A ' + lbl + ' square is likely larger than most entries need. Works best in grand foyers.' };
      }
      return { tone: 'ideal', label: '', copy: '' };
    },

    oval: function (room, w, h) {
      var lng = Math.max(w, h), sh = Math.min(w, h);
      var dims = fmtSize(w, h);
      if (room === 'dining') {
        if (lng < 9) return { tone: 'small', label: 'Too Small', copy: 'An oval this size sits under the table only — chairs slip off when pulled out. Aim for 9\' × 12\' or larger.' };
        if (lng < 11 || sh < 7) return { tone: 'tight', label: 'Tight', copy: 'An ' + dims + ' oval gives chairs a partial landing for a 4\' × 7\' table. 9\' × 12\' is more forgiving.' };
        if (lng < 14) return { tone: 'ideal', label: 'Ideal Fit', copy: 'An ' + dims + ' oval extends roughly 30" past a 4\' × 7\' table — chairs stay on when pulled out.' };
        return              { tone: 'tight', label: 'Oversized',  copy: 'An ' + dims + ' oval is great-room scale. For a typical dining room, 9\' × 12\' is the sweet spot.' };
      }
      if (room === 'living') {
        if (lng < 7)  return { tone: 'small', label: 'Too Small',  copy: 'An ' + dims + ' oval reads as an accent in front of a 7\' sofa. For a proper anchor, aim for 8\' × 10\' or larger.' };
        if (lng < 9)  return { tone: 'tight', label: 'Front Legs', copy: 'An ' + dims + ' oval lets the front legs of the sofa rest on — a looser, partial anchor.' };
        if (lng < 13) return { tone: 'ideal', label: 'Ideal Fit',  copy: 'An ' + dims + ' oval anchors a seating group with the coffee table fully on and front legs of the sofa and chair on the rug.' };
        return              { tone: 'tight', label: 'Large',        copy: 'An ' + dims + ' oval is great-room scale. Check that the rug clears walking lanes.' };
      }
      if (room === 'bedroom') {
        if (lng < 6)   return { tone: 'small', label: 'Bedside Accent',  copy: 'An ' + dims + ' oval reads as a small accent at the foot of a king. For a true foot-of-bed rug, go 5×8 or larger.' };
        if (lng < 9)   return { tone: 'ideal', label: 'Foot of Bed',     copy: 'An ' + dims + ' oval anchors the foot of the bed — extends past the foot and reaches under the mattress.' };
        if (lng < 13)  return { tone: 'ideal', label: 'Anchors the Bed', copy: 'An ' + dims + ' oval reaches well under the bed with the rug visible past the sides and extending past the foot. Nightstands sit off.' };
        return               { tone: 'tight', label: 'Too Large',       copy: 'An ' + dims + ' oval is larger than most bedrooms can hold. Consider 8×10 or 9×12 for a generous look.' };
      }
      if (room === 'entry') {
        if (lng < 5) return { tone: 'small', label: 'Too Small', copy: 'An ' + dims + ' oval reads as an accent. For a proper foyer, aim for 5\' × 7\' or larger.' };
        if (lng < 9) return { tone: 'ideal', label: 'Ideal Fit', copy: 'An ' + dims + ' oval anchors a foyer beneath a console or chandelier. Check door-swing clearance.' };
        return             { tone: 'tight', label: 'Large',      copy: 'An ' + dims + ' oval suits a long gallery foyer. In a typical entry, 6\' × 9\' is the sweet spot.' };
      }
      return { tone: 'ideal', label: '', copy: '' };
    },

    corner: function (room, w /*, h equal */) {
      var d = w;
      var dims = fmtFt(d) + ' × ' + fmtFt(d);
      if (room === 'reading') {
        if (d < 5) return { tone: 'small', label: 'Too Small', copy: 'A ' + dims + ' corner reads as an accent under a chair. 6\'–8\' lets the chair and side table sit fully on.' };
        if (d < 9) return { tone: 'ideal', label: 'Ideal Fit', copy: 'A ' + dims + ' corner anchors a reading nook — the chair tucks in and the rug softens the corner.' };
        return            { tone: 'tight', label: 'Large',     copy: 'A ' + dims + ' corner is generous for a single chair. Beautiful if the corner is deep; consider layering otherwise.' };
      }
      if (room === 'l-sofa') {
        if (d < 8)  return { tone: 'small', label: 'Too Small', copy: 'A ' + dims + ' corner reads small inside an 8\' × 6\' L-sectional. 9\'–11\' lets the rug fill the inside of the L and catch the front legs of the seating.' };
        if (d < 13) return { tone: 'ideal', label: 'Ideal Fit', copy: 'A ' + dims + ' corner fills the inside of the L beautifully — front legs of the sectional land on the rug.' };
        return            { tone: 'tight', label: 'Large',     copy: 'A ' + dims + ' corner is great-room scale. Check that the arc clears walking lanes past the sectional.' };
      }
      if (room === 'entry-corner') {
        if (d < 4) return { tone: 'small', label: 'Too Small', copy: 'A ' + dims + ' corner reads as an accent. For an entry corner, aim for 5\'–7\'.' };
        if (d < 8) return { tone: 'ideal', label: 'Ideal Fit', copy: 'A ' + dims + ' corner anchors a foyer corner — check that the arc clears the door swing.' };
        return            { tone: 'tight', label: 'Too Large', copy: 'A ' + dims + ' corner likely crosses into the door swing. Works best in grand entries.' };
      }
      return { tone: 'ideal', label: '', copy: '' };
    },

    runner: function (room, w, h) {
      var nw = Math.min(w, h), lg = Math.max(w, h);
      var dims = fmtFt(nw) + ' × ' + fmtFt(lg);
      if (room === 'hallway') {
        if (nw >= 3) return { tone: 'tight', label: 'Wide',  copy: 'A ' + fmtFt(nw) + '-wide runner leaves only a narrow margin in a 4\' hallway. 2\' or 2.5\' is the usual sweet spot.' };
        if (lg < 8)  return { tone: 'small', label: 'Short', copy: 'A ' + fmtFt(lg) + ' runner covers only part of a typical hall. 10–12\' looks intentional end-to-end.' };
        if (lg > 14) return { tone: 'tight', label: 'Long',  copy: 'A ' + fmtFt(lg) + ' runner suits a gallery-length hall. In a standard hall, 10–12\' is the usual fit.' };
        return             { tone: 'ideal', label: 'Ideal Fit', copy: 'A ' + dims + ' runner leaves a clean margin on each side and stops short of the doors.' };
      }
      if (room === 'kitchen') {
        if (nw >= 3) return { tone: 'tight', label: 'Wide',  copy: 'A ' + fmtFt(nw) + '-wide runner fills most of a 3.5\' galley aisle. 2\' or 2.5\' leaves room for cabinet doors.' };
        if (lg < 8)  return { tone: 'small', label: 'Short', copy: 'A ' + fmtFt(lg) + ' runner reads as an accent in front of one appliance. 10–12\' covers sink + range.' };
        if (lg > 14) return { tone: 'tight', label: 'Long',  copy: 'A ' + fmtFt(lg) + ' runner suits a very long kitchen. Most galleys want 10–12\'.' };
        return             { tone: 'ideal', label: 'Ideal Fit', copy: 'A ' + dims + ' runner covers the galley aisle in front of sink and range with room to step aside.' };
      }
      if (room === 'foot-of-bed') {
        if (lg < 7)  return { tone: 'small', label: 'Short',    copy: 'A ' + fmtFt(lg) + ' runner doesn\'t fully span the foot of a king (6.5\' wide). Aim for 8–10\' for full coverage.' };
        if (lg > 12) return { tone: 'tight', label: 'Long',     copy: 'A ' + fmtFt(lg) + ' runner extends well past the bed. Beautiful in wide rooms; in a standard master, 8–10\' looks more proportional.' };
        if (nw >= 3) return { tone: 'ideal', label: 'Generous', copy: 'A ' + fmtFt(nw) + '-wide runner gives a generous step-out at the foot of the bed — more substantial than a traditional runner.' };
        return             { tone: 'ideal', label: 'Ideal Fit',  copy: 'A ' + dims + ' runner spans the foot of a king with a soft landing on both sides of the bed.' };
      }
      if (room === 'landing') {
        if (lg < 6)  return { tone: 'small', label: 'Short', copy: 'A ' + fmtFt(lg) + ' runner covers the landing but leaves the hallway beyond bare. 8–10\' reads as one continuous path.' };
        if (nw >= 3) return { tone: 'tight', label: 'Wide',  copy: 'A ' + fmtFt(nw) + '-wide runner is wider than a typical 4\' landing allows. Check clearance to walls.' };
        if (lg > 14) return { tone: 'tight', label: 'Long',  copy: 'A ' + fmtFt(lg) + ' runner reaches far past the landing into the adjacent hallway. Confirm the hall run is long enough.' };
        return             { tone: 'ideal', label: 'Ideal Fit', copy: 'A ' + dims + ' runner reads as a clean path leading off the landing.' };
      }
      return { tone: 'ideal', label: '', copy: '' };
    }
  };

  // irregular reuses rectangle's fit verdict logic
  FIT_VERDICT.irregular = FIT_VERDICT.rectangle;

  /* ----------------------------------------------------------
     Mount & render
     ---------------------------------------------------------- */
  function init(root) {
    var dataId = root.getAttribute('data-mount');
    var dataEl = document.querySelector(
      'script[data-sgj-data="' + dataId + '"]'
    );
    if (!dataEl) { return; }
    var data;
    try { data = JSON.parse(dataEl.textContent); }
    catch (e) { return; }

    var shape = data.shape;
    if (!ROOM_DIMS[shape]) { return; }

    // Parse dims from each size label, e.g. "8' × 10'" → w:8, h:10.
    // Handles decimals ("2.5' × 10'") and both apostrophe/prime glyphs.
    var sizes = (data.sizes || []).map(function (s) {
      var m = String(s.label).match(
        /^\s*(\d+(?:\.\d+)?)\s*['′]?\s*[×x]\s*(\d+(?:\.\d+)?)/
      );
      var w = m ? parseFloat(m[1]) : 0;
      var h = m ? parseFloat(m[2]) : 0;
      return { label: s.label, w: w, h: h, silhouette: s.silhouette };
    }).filter(function (s) { return s.w > 0 && s.h > 0; });

    var state = {
      shape: shape,
      sizes: sizes,
      rooms: data.rooms || [],
      room: data.initialRoom || (data.rooms[0] && data.rooms[0].key),
      sizeLabel: data.initialSize || (sizes[0] && sizes[0].label),
      landscape: true  // only used for rectangle/oval
    };

    // Tabs
    root.querySelectorAll('[data-sgj-tab]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.room = btn.getAttribute('data-sgj-tab');
        render(root, state);
      });
    });

    // Chips
    root.querySelectorAll('[data-sgj-size]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.sizeLabel = btn.getAttribute('data-sgj-size');
        render(root, state);
      });
    });

    // Orientation (rect / oval)
    root.querySelectorAll('[data-sgj-orientation]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.landscape = btn.getAttribute('data-sgj-orientation') === 'landscape';
        render(root, state);
      });
    });

    // "See all N sizes" disclosure
    var toggleBtn = root.querySelector('[data-sgj-toggle-all]');
    var chipsAll = root.querySelector('[data-sgj-chips-all]');
    if (toggleBtn && chipsAll) {
      toggleBtn.addEventListener('click', function () {
        var expanded = toggleBtn.getAttribute('aria-expanded') === 'true';
        var next = !expanded;
        toggleBtn.setAttribute('aria-expanded', next ? 'true' : 'false');
        if (next) { chipsAll.removeAttribute('hidden'); }
        else      { chipsAll.setAttribute('hidden', ''); }
        var showLabel = toggleBtn.querySelector('[data-sgj-toggle-show]');
        var hideLabel = toggleBtn.querySelector('[data-sgj-toggle-hide]');
        if (showLabel && hideLabel) {
          if (next) {
            showLabel.setAttribute('hidden', '');
            hideLabel.removeAttribute('hidden');
          } else {
            hideLabel.setAttribute('hidden', '');
            showLabel.removeAttribute('hidden');
          }
        }
      });
    }

    render(root, state);
  }

  function render(root, state) {
    var shape = state.shape;
    var size = findSize(state.sizes, state.sizeLabel) || state.sizes[0];
    if (!size) { return; }

    // Effective [w, h] after orientation. Only rectangle/oval/irregular
    // respect the landscape toggle; other shapes render the size as stored.
    var isSquare = size.w === size.h;
    var longEdge = Math.max(size.w, size.h);
    var shortEdge = Math.min(size.w, size.h);
    var eff;
    if (isSquare || (shape !== 'rectangle' && shape !== 'oval' && shape !== 'irregular')) {
      eff = { w: size.w, h: size.h };
    } else if (state.landscape) {
      eff = { w: longEdge, h: shortEdge };
    } else {
      eff = { w: shortEdge, h: longEdge };
    }

    // Canvas dims
    var canvas = root.querySelector('[data-sgj-canvas]');
    if (canvas) {
      canvas.innerHTML = drawCanvas(shape, state.room, eff, size.silhouette);
      var dims = ROOM_DIMS[shape][state.room];
      if (dims) {
        var w = dims.w * PPF, h = dims.h * PPF;
        canvas.setAttribute('width', w);
        canvas.setAttribute('height', h);
        canvas.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
      }
    }

    // Reference caption
    var ref = root.querySelector('[data-sgj-reference]');
    if (ref) {
      var roomObj = findRoom(state.rooms, state.room);
      ref.textContent = roomObj ? ('Reference · ' + roomObj.reference) : '';
    }

    // Tabs — aria-selected
    root.querySelectorAll('[data-sgj-tab]').forEach(function (btn) {
      btn.setAttribute(
        'aria-selected',
        btn.getAttribute('data-sgj-tab') === state.room ? 'true' : 'false'
      );
    });

    // Chips — aria-pressed. Compare via normalized key so the popular-sizes
    // table ("8' × 8'") and the variant option values ("8' x 8'") match.
    var activeKey = sizeKey(size.label);
    root.querySelectorAll('[data-sgj-size]').forEach(function (btn) {
      btn.setAttribute(
        'aria-pressed',
        sizeKey(btn.getAttribute('data-sgj-size')) === activeKey ? 'true' : 'false'
      );
    });

    // Orientation — aria-pressed + visibility (hide for square sizes)
    var orientWrap = root.querySelector('[data-sgj-orientation-wrap]');
    if (orientWrap) {
      orientWrap.style.display = isSquare ? 'none' : '';
      root.querySelectorAll('[data-sgj-orientation]').forEach(function (btn) {
        var is = btn.getAttribute('data-sgj-orientation') === 'landscape'
          ? state.landscape : !state.landscape;
        btn.setAttribute('aria-pressed', is ? 'true' : 'false');
      });
    }

    // Fit verdict
    var fit = FIT_VERDICT[shape]
      ? FIT_VERDICT[shape](state.room, size.w, size.h)
      : { tone: 'ideal', label: '', copy: '' };
    var fitEl = root.querySelector('[data-sgj-fit]');
    if (fitEl) {
      fitEl.setAttribute('data-tone', fit.tone);
      var fitLabel = fitEl.querySelector('[data-sgj-fit-label]');
      var fitCopy = fitEl.querySelector('[data-sgj-fit-copy]');
      if (fitLabel) { fitLabel.textContent = fit.label; }
      if (fitCopy)  { fitCopy.textContent = fit.copy; }
    }
  }

  function drawCanvas(shape, room, eff, silhouetteUrl) {
    var dims = ROOM_DIMS[shape] && ROOM_DIMS[shape][room];
    if (!dims) { return ''; }
    var rw = dims.w, rh = dims.h;
    var canvasW = rw * PPF, canvasH = rh * PPF;

    var placer = PLACEMENT[shape];
    var placement = placer ? placer(room, eff, rw, rh) : null;

    var out = '';
    // Room boundary — dashed hairline
    out +=
      '<rect x="0.5" y="0.5" width="' + (canvasW - 1) +
      '" height="' + (canvasH - 1) +
      '" fill="none" stroke="' + BRONZE +
      '" stroke-width="1" stroke-dasharray="3 4" opacity="0.4"/>';

    // Rug
    if (placement) {
      out += drawRug(shape, placement, silhouetteUrl);
      // Bedside pair: draw the mirrored second runner on the far side.
      if (placement.pairX != null) {
        out += drawRug(shape, {
          x: placement.pairX, y: placement.y, w: placement.w, h: placement.h
        }, silhouetteUrl);
      }
    }

    // Furniture overlay
    var f = FURNITURE[shape] && FURNITURE[shape][room];
    if (f) { out += '<g>' + f(PPF, rw, rh) + '</g>'; }

    return out;
  }

  /* ----------------------------------------------------------
     Rug drawing — shape-aware. Corner gets its own path +
     clip; round/oval get clip-paths to mask the rectangular
     silhouette image.
     ---------------------------------------------------------- */
  function drawRug(shape, p, silhouetteUrl) {
    if (p.corner) {
      return drawCornerRug(p, silhouetteUrl);
    }
    var x = p.x * PPF, y = p.y * PPF, w = p.w * PPF, h = p.h * PPF;

    var clipDef = '', clipAttr = '';
    if (shape === 'round') {
      clipDef =
        '<defs><clipPath id="sgj-rug-clip"><circle cx="' + r2(x + w / 2) +
        '" cy="' + r2(y + h / 2) + '" r="' + r2(Math.min(w, h) / 2) +
        '"/></clipPath></defs>';
      clipAttr = ' clip-path="url(#sgj-rug-clip)"';
    } else if (shape === 'oval') {
      clipDef =
        '<defs><clipPath id="sgj-rug-clip"><ellipse cx="' + r2(x + w / 2) +
        '" cy="' + r2(y + h / 2) + '" rx="' + r2(w / 2) +
        '" ry="' + r2(h / 2) + '"/></clipPath></defs>';
      clipAttr = ' clip-path="url(#sgj-rug-clip)"';
    }

    var rugEl;
    if (silhouetteUrl) {
      // Silhouettes are uploaded portrait by convention. When bbox is
      // landscape, rotate the image 90° about the bbox top-right so the
      // design actually rotates instead of being stretched/cropped into
      // a wider rect. For oval, an outer (untransformed) group carries
      // the ellipse clip in user space so the clip stays put while the
      // image rotates inside.
      if ((shape === 'rectangle' || shape === 'oval' || shape === 'irregular') && w > h) {
        var rotated =
          '<g transform="translate(' + r2(x + w) + ' ' + r2(y) + ') rotate(90)">' +
            '<image href="' + escapeAttr(silhouetteUrl) +
            '" x="0" y="0" width="' + r2(h) + '" height="' + r2(w) +
            '" preserveAspectRatio="xMidYMid slice"/>' +
          '</g>';
        rugEl = clipAttr ? '<g' + clipAttr + '>' + rotated + '</g>' : rotated;
      } else {
        rugEl =
          '<image href="' + escapeAttr(silhouetteUrl) +
          '" x="' + r2(x) + '" y="' + r2(y) +
          '" width="' + r2(w) + '" height="' + r2(h) +
          '" preserveAspectRatio="xMidYMid slice"' + clipAttr + '/>';
      }
    } else {
      rugEl = rugPlaceholder(shape, x, y, w, h);
    }
    return clipDef + rugEl;
  }

  function drawCornerRug(p, silhouetteUrl) {
    var ax = p.anchorX * PPF, ay = p.anchorY * PPF, side = p.side * PPF;
    var imgX, imgY, clipD;
    switch (p.orient) {
      case 'bl':
        imgX = ax; imgY = ay - side;
        clipD =
          'M ' + ax + ' ' + ay +
          ' L ' + ax + ' ' + (ay - side) +
          ' A ' + side + ' ' + side + ' 0 0 0 ' + (ax + side) + ' ' + ay +
          ' Z';
        break;
      case 'tl':
        imgX = ax; imgY = ay;
        clipD =
          'M ' + ax + ' ' + ay +
          ' L ' + (ax + side) + ' ' + ay +
          ' A ' + side + ' ' + side + ' 0 0 1 ' + ax + ' ' + (ay + side) +
          ' Z';
        break;
      case 'tr':
        imgX = ax - side; imgY = ay;
        clipD =
          'M ' + ax + ' ' + ay +
          ' L ' + ax + ' ' + (ay + side) +
          ' A ' + side + ' ' + side + ' 0 0 1 ' + (ax - side) + ' ' + ay +
          ' Z';
        break;
      case 'br':
      default:
        imgX = ax - side; imgY = ay - side;
        clipD =
          'M ' + ax + ' ' + ay +
          ' L ' + (ax - side) + ' ' + ay +
          ' A ' + side + ' ' + side + ' 0 0 0 ' + ax + ' ' + (ay - side) +
          ' Z';
    }
    var clipDef =
      '<defs><clipPath id="sgj-rug-clip"><path d="' + clipD +
      '"/></clipPath></defs>';
    var rugEl;
    if (silhouetteUrl) {
      rugEl =
        '<image href="' + escapeAttr(silhouetteUrl) +
        '" x="' + r2(imgX) + '" y="' + r2(imgY) +
        '" width="' + r2(side) + '" height="' + r2(side) +
        '" preserveAspectRatio="xMidYMid slice" clip-path="url(#sgj-rug-clip)"/>';
    } else {
      rugEl =
        '<path d="' + clipD + '" fill="' + BG2 +
        '" stroke="' + INK + '" stroke-width="0.75" opacity="0.55"/>';
    }
    // Outline on top so the quarter-circle edge reads clearly
    var outline =
      '<path d="' + clipD +
      '" fill="none" stroke="' + INK +
      '" stroke-width="0.75" opacity="0.6"/>';
    return clipDef + rugEl + outline;
  }

  function rugPlaceholder(shape, x, y, w, h) {
    var attrs =
      'fill="none" stroke="' + INK +
      '" stroke-width="0.75" opacity="0.55"';
    if (shape === 'round') {
      var r = Math.min(w, h) / 2;
      return (
        '<circle cx="' + r2(x + w / 2) + '" cy="' + r2(y + h / 2) +
        '" r="' + r2(r) + '" ' + attrs + '/>'
      );
    }
    if (shape === 'oval') {
      return (
        '<ellipse cx="' + r2(x + w / 2) + '" cy="' + r2(y + h / 2) +
        '" rx="' + r2(w / 2) + '" ry="' + r2(h / 2) +
        '" ' + attrs + '/>'
      );
    }
    return (
      '<rect x="' + r2(x) + '" y="' + r2(y) +
      '" width="' + r2(w) + '" height="' + r2(h) +
      '" ' + attrs + '/>'
    );
  }

  /* ----------------------------------------------------------
     Lookup utilities
     ---------------------------------------------------------- */
  function findSize(list, label) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].label === label) { return list[i]; }
    }
    var key = sizeKey(label);
    for (var j = 0; j < list.length; j++) {
      if (sizeKey(list[j].label) === key) { return list[j]; }
    }
    return null;
  }
  function sizeKey(s) {
    return String(s || '')
      .replace(/['′]/g, '')
      .replace(/×/g, 'x')
      .replace(/\s+/g, '')
      .toLowerCase();
  }
  function findRoom(list, key) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].key === key) { return list[i]; }
    }
    return null;
  }

  /* ----------------------------------------------------------
     Boot — mount every [data-mount] under .sgj, and watch for
     late-inserted copies (e.g. drawer opens).
     ---------------------------------------------------------- */
  var mounted = new WeakSet();
  function mountAll() {
    document.querySelectorAll('.sgj[data-mount]').forEach(function (r) {
      if (mounted.has(r)) { return; }
      mounted.add(r);
      init(r);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }
  new MutationObserver(mountAll).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
